/**
 * What the side panels' drafts amount to (I-3024): the checks master's
 * validator makes — the word rule, fold twins inside a layer, 2–50 terms, a
 * term in one group of a language and layer — done before anything is sent,
 * so the panel names the field and the other entry in words, and the merge
 * patch of a save. Only the entries of the layer that is written count: a
 * farther layer's word is no twin (the nearer layer wins). Pure functions.
 */
import { mergeJson, type JsonObject } from '../../config/mergePatch';
import {
  searchSynonymPatch,
  type SearchSynonymEntry,
  type SearchSynonymsConfig,
} from '../../config/searchSynonyms';
import {
  spellCorrectionPatch,
  type SpellCheckConfig,
  type SpellCorrectionEntry,
} from '../../config/spellCheck';
import {
  DICTIONARY_MAX_WORD,
  SYNONYM_MAX_TERMS,
  SYNONYM_MIN_TERMS,
  dictionaryWord,
  type DictionaryLanguage,
} from '../dictionaryFile';
import { dictionaryFold } from '../dictionaryFold';

/** The first thing wrong with a draft, with the field it belongs to. */
export interface DraftProblem {
  field: 'typed' | 'to' | 'name' | 'source' | 'terms';
  code: 'word_rule' | 'same_word' | 'fold_twin' | 'term_in_two_groups' | 'terms_count';
  /** Values the text names (`other`, `term`, `group`, `max`, `min`). */
  params: Record<string, unknown>;
  /** The group that holds a term (`term_in_two_groups`): the panel offers to open it. */
  group?: string;
}

const wordRule = (field: DraftProblem['field']): DraftProblem => ({
  field,
  code: 'word_rule',
  params: { max: DICTIONARY_MAX_WORD },
});

/** A draft's patch with the entry it replaced removed: the old key, in the old language. */
function movedFrom(removal: JsonObject, patch: JsonObject): JsonObject {
  return mergeJson(removal, patch) as JsonObject;
}

/* ── Corrections ─────────────────────────────────────────────────────────── */

export interface CorrectionDraft {
  typed: string;
  to: string;
  lang: DictionaryLanguage;
}

/** The entry a draft edits (`undefined`: a new one). */
export interface CorrectionOriginal {
  lang: DictionaryLanguage;
  typed: string;
}

/** `own`: the corrections of the written layer. */
export function correctionDraftProblem(
  draft: CorrectionDraft,
  own: readonly SpellCorrectionEntry[],
  editing?: CorrectionOriginal,
): DraftProblem | null {
  const typed = dictionaryWord(draft.typed);
  const to = dictionaryWord(draft.to);
  if (!typed) return wordRule('typed');
  if (!to) return wordRule('to');
  if (dictionaryFold(typed) === dictionaryFold(to)) return { field: 'to', code: 'same_word', params: {} };
  const key = dictionaryFold(typed);
  const twin = own.find(
    (entry) =>
      entry.lang === draft.lang &&
      dictionaryFold(entry.typed) === key &&
      !(editing && editing.lang === entry.lang && editing.typed === entry.typed),
  );
  if (twin) return { field: 'typed', code: 'fold_twin', params: { other: twin.typed } };
  return null;
}

/** The merge patch of a saved correction: typed in, so origin own and not counted. */
export function correctionDraftPatch(draft: CorrectionDraft, editing?: CorrectionOriginal): SpellCheckConfig {
  const typed = dictionaryWord(draft.typed) ?? draft.typed;
  const value = { to: dictionaryWord(draft.to) ?? draft.to, count: 0, origin: 'own' as const };
  if (editing && editing.lang !== draft.lang) {
    return movedFrom(
      { corrections: { [editing.lang]: { [editing.typed]: null } } },
      spellCorrectionPatch(draft.lang, typed, value) as JsonObject,
    ) as SpellCheckConfig;
  }
  return spellCorrectionPatch(draft.lang, typed, value, editing?.typed);
}

/* ── Synonym groups ──────────────────────────────────────────────────────── */

export interface SynonymDraft {
  name: string;
  lang: DictionaryLanguage;
  oneWay: boolean;
  /** The source of a one-way group (its first term). */
  source: string;
  /** The terms: all of a two-way group, the ones the source also finds for a one-way group. */
  terms: readonly string[];
}

export interface SynonymOriginal {
  lang: DictionaryLanguage;
  name: string;
}

/** The group's terms as stored: the source first for a one-way group, each word once by fold. */
export function synonymDraftTerms(draft: SynonymDraft): string[] {
  const all = draft.oneWay ? [draft.source, ...draft.terms] : [...draft.terms];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const term of all) {
    const word = dictionaryWord(term);
    if (!word) continue;
    const key = dictionaryFold(word);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(word);
  }
  return out;
}

/** `own`: the groups of the written layer. */
export function synonymDraftProblem(
  draft: SynonymDraft,
  own: readonly SearchSynonymEntry[],
  editing?: SynonymOriginal,
): DraftProblem | null {
  const name = dictionaryWord(draft.name);
  if (!name) return wordRule('name');
  if (draft.oneWay && !dictionaryWord(draft.source)) return wordRule('source');
  const given = draft.oneWay ? [draft.source, ...draft.terms] : [...draft.terms];
  if (given.some((term) => term.trim() !== '' && !dictionaryWord(term))) return wordRule('terms');
  const terms = synonymDraftTerms(draft);
  if (terms.length < SYNONYM_MIN_TERMS || terms.length > SYNONYM_MAX_TERMS) {
    return { field: 'terms', code: 'terms_count', params: { min: SYNONYM_MIN_TERMS, max: SYNONYM_MAX_TERMS } };
  }
  const others = own.filter(
    (entry) => entry.lang === draft.lang && !(editing && editing.lang === entry.lang && editing.name === entry.name),
  );
  const twin = others.find((entry) => dictionaryFold(entry.name) === dictionaryFold(name));
  if (twin) return { field: 'name', code: 'fold_twin', params: { other: twin.name } };
  for (const term of terms) {
    const key = dictionaryFold(term);
    const holder = others.find((entry) => entry.terms.some((candidate) => dictionaryFold(candidate) === key));
    if (holder) {
      // The term as the person typed it, not as the dedupe kept it.
      const typed = given.find((candidate) => dictionaryFold(candidate.trim()) === key) ?? term;
      return {
        field: 'terms',
        code: 'term_in_two_groups',
        params: { term: typed.trim(), group: holder.name },
        group: holder.name,
      };
    }
  }
  return null;
}

/** The merge patch of a saved group: typed in, so origin own. */
export function synonymDraftPatch(draft: SynonymDraft, editing?: SynonymOriginal): SearchSynonymsConfig {
  const name = dictionaryWord(draft.name) ?? draft.name;
  const value = { terms: synonymDraftTerms(draft), oneWay: draft.oneWay, origin: 'own' as const };
  if (editing && editing.lang !== draft.lang) {
    return movedFrom(
      { synonyms: { [editing.lang]: { [editing.name]: null } } },
      searchSynonymPatch(draft.lang, name, value) as JsonObject,
    ) as SearchSynonymsConfig;
  }
  return searchSynonymPatch(draft.lang, name, value, editing?.name);
}
